import type { AlertSound } from "./alert-sound.js";

type Listenable = Pick<EventTarget, "addEventListener" | "removeEventListener">;

/** Gestures that count as user activation for audio autoplay. pointerdown covers
 *  mouse/touch/pen; touchstart is a fallback; keydown covers keyboard users. */
const GESTURES = ["pointerdown", "touchstart", "keydown"] as const;

/** Browsers block audio until the user interacts with the page. After a reload no gesture
 *  has happened yet, so the AudioContext is suspended and an auto-firing alert (e.g. a
 *  critical takeover shown as incidents load) is silent. This arms audio on the user's
 *  FIRST interaction so every later alert can sound. Idempotent: fires unlock once, then
 *  removes itself. Returns a teardown that detaches the listeners early. */
export function installAudioPrimer(
  target: Listenable,
  alertSound: Pick<AlertSound, "unlock">,
): () => void {
  const teardown = (): void => {
    for (const g of GESTURES) target.removeEventListener(g, onGesture);
  };
  const onGesture = (): void => {
    teardown();
    alertSound.unlock();
  };
  for (const g of GESTURES) {
    target.addEventListener(g, onGesture, { once: true, passive: true } as AddEventListenerOptions);
  }
  return teardown;
}
